import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import { templateAccess } from "./meetingTemplateForm";

export default function MeetingTemplateEditor() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canAdvisory, canTeam, canList } = templateAccess(user);
  const defaultType = canAdvisory ? "advisory" : "internal_1_1";
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!canList) return;
    api.get("/meeting-templates").then((r) => setList(r.data)).catch(() => setList([]));
  }, [canList]);

  if (!canList) {
    return (
      <div className="tb-page">
        <h1 className="tb-h1">{t("settings.templates.title")}</h1>
        <p className="tb-muted">{t("settings.templates.principalOnly")}</p>
      </div>
    );
  }
  if (list === null) return <div className="tb-loading">{t("common.loading")}</div>;

  const visible = list;
  const groupLabel = {
    advisor: t("settings.templates.tabAdvisory"),
    accelerator: t("settings.templates.tabAccelerator"),
    investor: t("settings.templates.tabInvestor"),
    provider: t("settings.templates.tabProvider"),
    teammate: t("settings.templates.tabTeammate"),
  };
  const afterWho = {
    advisor: t("settings.templates.whoAdvisor"),
    accelerator: t("settings.templates.whoAccelerator"),
    investor: t("settings.templates.whoInvestor"),
    provider: t("settings.templates.whoProvider"),
    teammate: t("settings.templates.whoEach"),
  };
  const rowsByAudience = (aud) => visible.filter((tpl) => (tpl.audience || (tpl.session_type === "internal_1_1" ? "teammate" : "advisor")) === aud);
  const presentGroups = ["advisor", "accelerator", "investor", "provider", "teammate"].filter((aud) => rowsByAudience(aud).length > 0);
  const showGroupHeadings = presentGroups.length > 1;

  const duplicate = async (id) => {
    try {
      const r = await api.post(`/meeting-templates/${id}/duplicate`);
      navigate(`/app/settings/templates/build/${r.data.id}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("settings.templates.confirmDelete"))) return;
    try {
      await api.delete(`/meeting-templates/${id}`);
      setList((rows) => (rows || []).filter((x) => x.id !== id));
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const countBit = (n, phase, who) => t("settings.templates.countBit", {
    n,
    word: n === 1 ? t("settings.templates.question") : t("settings.templates.questions"),
    phase,
    who,
  });

  const Card = ({ tpl }) => {
    const aud = tpl.audience || ((tpl.session_type || "internal_1_1") === "advisory" ? "advisor" : "teammate");
    const beforeN = (tpl.before_questions || tpl.questions || []).length;
    const duringN = (tpl.during_note_prompts || []).length;
    const afterN = (tpl.after_questions || []).length;
    const whoBefore = aud === "teammate" ? t("settings.templates.whoEach") : t("settings.templates.whoFounder");
    const whoDuring = t("settings.templates.whoBoth");
    const whoAfter = afterWho[aud] || t("settings.templates.whoAdvisor");
    return (
    <div className="tb-block" data-testid={`template-card-${tpl.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <strong style={{ fontSize: "1rem" }}>{tpl.name}</strong>
        <span>
          {tpl.is_default && <span className="tb-badge accent">{t("settings.templates.default")}</span>}
          {tpl.is_system && <span className="tb-badge muted" style={{ marginLeft: 6 }}>{t("settings.templates.system")}</span>}
        </span>
      </div>
      {tpl.description && (
        <p className="tb-muted" style={{ fontSize: "0.85rem", marginTop: 8 }}>
          {tpl.description.length > 160 ? `${tpl.description.slice(0, 160)}…` : tpl.description}
        </p>
      )}
      <p className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 8, marginBottom: 0 }}>
        {countBit(beforeN, t("settings.templates.beforeCount"), whoBefore)}
        {" · "}
        {countBit(duringN, t("settings.templates.duringCount"), whoDuring)}
        {" · "}
        {countBit(afterN, t("settings.templates.afterCount"), whoAfter)}
      </p>
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        {!tpl.is_system && (
          <Link
            to={`/app/settings/templates/build/${tpl.id}`}
            className="tb-ghost-btn"
            style={{ textDecoration: "none", padding: "6px 12px", fontSize: "0.82rem" }}
            data-testid={`edit-template-${tpl.id}`}
          >
            {t("settings.templates.edit")}
          </Link>
        )}
        <button type="button" className="tb-ghost-btn" style={{ padding: "6px 12px", fontSize: "0.82rem" }} onClick={() => duplicate(tpl.id)} data-testid={`duplicate-template-${tpl.id}`}>
          {t("settings.templates.duplicate")}
        </button>
        {!tpl.is_system && (
          <button type="button" className="tb-ghost-btn" style={{ padding: "6px 12px", fontSize: "0.82rem" }} onClick={() => remove(tpl.id)} data-testid={`delete-template-${tpl.id}`}>
            {t("settings.templates.delete")}
          </button>
        )}
      </div>
    </div>
    );
  };

  return (
    <div className="tb-page" data-testid="templates-page">
      <button className="tb-ghost-btn" onClick={() => navigate("/app/settings")} style={{ marginBottom: 16 }}>
        ← {t("settings.title")}
      </button>
      <h1 className="tb-h1" data-testid="templates-heading">{t("settings.templates.title")}</h1>
      <p className="tb-muted" style={{ maxWidth: 760, marginBottom: 16 }}>{t("settings.templates.hint")}</p>

      {err && <div className="tb-form-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div className="tb-cta-row">
        <Link
          to={`/app/settings/templates/build?type=${defaultType}`}
          className="tb-primary-btn"
          style={{ textDecoration: "none", color: "#0a0d12", display: "inline-block" }}
          data-testid="new-template"
        >
          {t("settings.templates.new")}
        </Link>
        <Link
          to={`/app/settings/templates/import?type=${defaultType}`}
          className="tb-ghost-btn"
          style={{ textDecoration: "none", display: "inline-block" }}
          data-testid="toggle-md-import"
        >
          {t("settings.templates.importMd")}
        </Link>
      </div>

      {visible.length === 0 ? (
        <p className="tb-muted">{t("settings.templates.empty")}</p>
      ) : (
        <div data-testid="templates-list">
          {presentGroups.map((aud) => (
            <div key={aud}>
              {showGroupHeadings && (
                <h2 className="tb-h2" style={{ marginTop: aud === presentGroups[0] ? 8 : 28, marginBottom: 12 }}>{groupLabel[aud]}</h2>
              )}
              <div className="tb-card-grid" data-testid={`templates-list-${aud}`}>
                {rowsByAudience(aud).map((tpl) => <Card key={tpl.id} tpl={tpl} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
